import React from 'react';
import Icon from '../../../components/AppIcon';

// ---------------------------------------------------------------------------
// CaseloadSummaryTiles — the top row of the AI Insights page.
//
// Four tiles, all computed from analyzeCaseload().summary. No CTAs here
// (they'd fight for attention with the Recommendations column below).
// The tiles are read-only, chart-adjacent hero numbers so a teacher can
// glance and know "am I in trouble this week or not."
//
// Each tile carries an icon + a color that matches the signal it
// represents:
//   - green  → the tile is a positive/neutral fact ("N students on track")
//   - amber  → the tile is something to watch
//   - red    → the tile is something to act on right now
// ---------------------------------------------------------------------------

function Tile({ icon, label, value, sublabel, tone = 'neutral' }) {
  const toneClasses = {
    neutral: 'bg-slate-50 border-slate-200 text-slate-900',
    good: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    warn: 'bg-amber-50 border-amber-200 text-amber-900',
    bad: 'bg-red-50 border-red-200 text-red-900',
  }[tone];

  const iconClasses = {
    neutral: 'text-slate-500',
    good: 'text-emerald-600',
    warn: 'text-amber-600',
    bad: 'text-red-600',
  }[tone];

  return (
    <div className={`rounded-lg border p-4 ${toneClasses}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon} size={16} className={iconClasses} />
        <span className="text-xs font-medium uppercase tracking-wide opacity-80">
          {label}
        </span>
      </div>
      <div className="text-3xl font-semibold leading-tight">{value}</div>
      {sublabel && (
        <div className="text-xs opacity-75 mt-1">{sublabel}</div>
      )}
    </div>
  );
}

const CaseloadSummaryTiles = ({ summary }) => {
  if (!summary) return null;

  const attentionCount =
    (summary.studentsBySignal?.critical || 0) + (summary.studentsBySignal?.attention || 0);

  const incidentDelta = summary.incidentsThisWeek - summary.incidentsLastWeek;
  const incidentSubtext =
    summary.incidentsLastWeek === 0 && summary.incidentsThisWeek === 0
      ? 'none in the last two weeks'
      : incidentDelta === 0
        ? 'unchanged vs. last week'
        : incidentDelta > 0
          ? `${incidentDelta} more than last week`
          : `${Math.abs(incidentDelta)} fewer than last week`;

  const reviewSubtext =
    summary.reviewsOverdue > 0
      ? `${summary.reviewsOverdue} already overdue`
      : summary.reviewsDueSoon > 0
        ? `${summary.reviewsDueSoon} due in ≤ 30 days`
        : 'nothing on the horizon';

  const goalSubtext = summary.goalsMastered > 0
    ? `${summary.goalsMastered} mastered so far`
    : 'no mastered goals yet';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Tile
        icon="Users"
        label="Students needing a look"
        value={attentionCount}
        sublabel={`of ${summary.totalStudents} on your caseload`}
        tone={attentionCount === 0 ? 'good' : summary.studentsBySignal?.critical > 0 ? 'bad' : 'warn'}
      />
      <Tile
        icon="Target"
        label="Active IEP goals"
        value={summary.activeGoals}
        sublabel={goalSubtext}
        tone="neutral"
      />
      <Tile
        icon="AlertTriangle"
        label="Incidents this week"
        value={summary.incidentsThisWeek}
        sublabel={incidentSubtext}
        tone={
          summary.incidentsThisWeek === 0
            ? 'good'
            : incidentDelta > 0 && summary.incidentsThisWeek >= 3
              ? 'bad'
              : incidentDelta > 0
                ? 'warn'
                : 'neutral'
        }
      />
      <Tile
        icon="CalendarClock"
        label="IEP reviews on the horizon"
        value={summary.reviewsOverdue + summary.reviewsDueSoon}
        sublabel={reviewSubtext}
        tone={summary.reviewsOverdue > 0 ? 'bad' : summary.reviewsDueSoon > 0 ? 'warn' : 'good'}
      />
    </div>
  );
};

export default CaseloadSummaryTiles;
